import { lazy, Suspense } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { Toaster } from 'react-hot-toast'
import { SentryErrorBoundary } from '@/lib/sentry'
import { useAuth } from '@/hooks/useAuth'
import { supabase } from '@/lib/supabase'

import Layout      from '@/components/layout/Layout'
import Landing     from '@/pages/Landing'
import AdminRoute  from '@/components/AdminRoute'

const Login          = lazy(() => import('@/pages/Login'))
const Register       = lazy(() => import('@/pages/Register'))
const ForgotPassword = lazy(() => import('@/pages/ForgotPassword'))
const ResetPassword  = lazy(() => import('@/pages/ResetPassword'))
const ConfirmEmail   = lazy(() => import('@/pages/ConfirmEmail'))
const CGU            = lazy(() => import('@/pages/legal/CGU'))
const Confidentialite = lazy(() => import('@/pages/legal/Confidentialite'))
const Privacy         = lazy(() => import('@/pages/Privacy'))
const Mentions       = lazy(() => import('@/pages/legal/Mentions'))
const Dashboard      = lazy(() => import('@/pages/Dashboard'))
const Vivier         = lazy(() => import('@/pages/Vivier'))
const Mandats        = lazy(() => import('@/pages/Mandats'))
const Commissions    = lazy(() => import('@/pages/Commissions'))
const Missions       = lazy(() => import('@/pages/Missions'))
const Pipeline       = lazy(() => import('@/pages/Pipeline'))
const Collaborations = lazy(() => import('@/pages/Collaborations'))
const Partners       = lazy(() => import('@/pages/Partners'))
const Activite           = lazy(() => import('@/pages/Activite'))
const Settings           = lazy(() => import('@/pages/Settings'))
const Facturation        = lazy(() => import('@/pages/Facturation'))
const FacturationNouvelle = lazy(() => import('@/pages/FacturationNouvelle'))
const FacturationDetail  = lazy(() => import('@/pages/FacturationDetail'))
const Clients            = lazy(() => import('@/pages/Clients'))
const AdminAnalytics     = lazy(() => import('@/pages/admin/Analytics'))
const AdminUsers         = lazy(() => import('@/pages/admin/Users'))
const AdminUserDetail    = lazy(() => import('@/pages/admin/UserDetail'))
const Declaration        = lazy(() => import('@/pages/Declaration'))
const Pricing            = lazy(() => import('@/pages/Pricing'))
const ClientIntake       = lazy(() => import('@/pages/ClientIntake'))
const PartnerIntake      = lazy(() => import('@/pages/PartnerIntake'))

function PageLoader() {
  return (
    <div className="flex-1 flex items-center justify-center">
      <div className="w-8 h-8 border-4 border-[#4F46E5] border-t-transparent rounded-full animate-spin" />
    </div>
  )
}

function AppSpinner() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8F9FC] dark:bg-gray-950">
      <div className="flex flex-col items-center gap-4">
        <div className="w-10 h-10 border-4 border-[#4F46E5] border-t-transparent rounded-full animate-spin" />
        <p className="text-sm text-gray-500 dark:text-gray-400 font-medium">Chargement...</p>
      </div>
    </div>
  )
}

// Redirige vers /app si déjà connecté (login, register, landing)
function PublicRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth()
  if (loading) return <AppSpinner />
  if (user) return <Navigate to="/app" replace />
  return <>{children}</>
}

// Redirige vers /login si non connecté, vers /confirm-email si email non vérifié
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth()
  if (loading) return <AppSpinner />
  if (!user) return <Navigate to="/login" replace />

  // Vérification email — on lit la session Supabase de façon synchrone via le cache local
  // email_confirmed_at est null tant que l'utilisateur n'a pas cliqué sur le lien
  const session = supabase.auth.getSession
  // On utilise le flag dans le localStorage plutôt que de bloquer sur une Promise
  // pour éviter un flash. Si Supabase a la confirmation désactivée, email_confirmed_at
  // est déjà renseigné au signup et le localStorage sera vide.
  const pendingEmail = localStorage.getItem('pending_email')
  if (pendingEmail) {
    // L'utilisateur vient de s'inscrire mais n'a pas encore cliqué sur le lien
    // Vérification asynchrone : si la session a bien email_confirmed_at, on nettoie
    supabase.auth.getSession().then(({ data }) => {
      if (data.session?.user?.email_confirmed_at) {
        localStorage.removeItem('pending_email')
      }
    })
    // Tant que pending_email est présent, bloquer l'accès
    return <Navigate to="/confirm-email" replace />
  }

  void session // satisfy linter
  return <>{children}</>
}

function ErrorFallback({ error }: { error: Error }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8F9FC] dark:bg-gray-950">
      <div className="text-center space-y-4 p-8">
        <p className="text-lg font-semibold text-gray-800 dark:text-gray-100">Une erreur inattendue s'est produite.</p>
        <p className="text-sm text-gray-500 dark:text-gray-400">{error.message}</p>
        <button
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-[#4F46E5] text-white text-sm rounded-lg hover:bg-[#4338CA] transition-colors"
        >
          Recharger la page
        </button>
      </div>
    </div>
  )
}

export default function App() {
  return (
    <SentryErrorBoundary fallback={({ error }) => <ErrorFallback error={error as Error} />}>
    <BrowserRouter>
      <Routes>
        {/* Landing page */}
        <Route path="/" element={<PublicRoute><Landing /></PublicRoute>} />

        {/* Auth */}
        <Route path="/login"           element={<PublicRoute><Suspense fallback={null}><Login /></Suspense></PublicRoute>} />
        <Route path="/register"        element={<PublicRoute><Suspense fallback={null}><Register /></Suspense></PublicRoute>} />
        <Route path="/forgot-password" element={<PublicRoute><Suspense fallback={null}><ForgotPassword /></Suspense></PublicRoute>} />
        {/* reset-password n'est PAS PublicRoute : Supabase peut rediriger un user déjà connecté */}
        <Route path="/reset-password"  element={<Suspense fallback={null}><ResetPassword /></Suspense>} />
        <Route path="/confirm-email"   element={<Suspense fallback={null}><ConfirmEmail /></Suspense>} />

        {/* Pages légales (publiques) */}
        <Route path="/legal/cgu"              element={<Suspense fallback={null}><CGU /></Suspense>} />
        <Route path="/legal/confidentialite"  element={<Suspense fallback={null}><Confidentialite /></Suspense>} />
        <Route path="/privacy"               element={<Suspense fallback={null}><Privacy /></Suspense>} />
        <Route path="/legal/mentions"         element={<Suspense fallback={null}><Mentions /></Suspense>} />

        {/* App protégée sous /app */}
        <Route
          path="/app"
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route index                      element={<Suspense fallback={<PageLoader />}><Dashboard /></Suspense>} />
          <Route path="vivier"              element={<Suspense fallback={<PageLoader />}><Vivier /></Suspense>} />
          <Route path="mandats"             element={<Suspense fallback={<PageLoader />}><Mandats /></Suspense>} />
          <Route path="commissions"         element={<Suspense fallback={<PageLoader />}><Commissions /></Suspense>} />
          <Route path="missions"            element={<Suspense fallback={<PageLoader />}><Missions /></Suspense>} />
          <Route path="pipeline"            element={<Suspense fallback={<PageLoader />}><Pipeline /></Suspense>} />
          <Route path="collaborations"      element={<Suspense fallback={<PageLoader />}><Collaborations /></Suspense>} />
          <Route path="clients"              element={<Suspense fallback={<PageLoader />}><Clients /></Suspense>} />
          <Route path="partenaires"         element={<Suspense fallback={<PageLoader />}><Partners /></Suspense>} />
          <Route path="activite"               element={<Suspense fallback={<PageLoader />}><Activite /></Suspense>} />
          <Route path="settings"             element={<Suspense fallback={<PageLoader />}><Settings /></Suspense>} />
          <Route path="facturation"          element={<Suspense fallback={<PageLoader />}><Facturation /></Suspense>} />
          <Route path="facturation/nouvelle" element={<Suspense fallback={<PageLoader />}><FacturationNouvelle /></Suspense>} />
          <Route path="facturation/:id"      element={<Suspense fallback={<PageLoader />}><FacturationDetail /></Suspense>} />
          <Route path="admin/analytics"   element={<AdminRoute><Suspense fallback={<PageLoader />}><AdminAnalytics /></Suspense></AdminRoute>} />
          <Route path="admin/users"       element={<AdminRoute><Suspense fallback={<PageLoader />}><AdminUsers /></Suspense></AdminRoute>} />
          <Route path="admin/users/:id"   element={<AdminRoute><Suspense fallback={<PageLoader />}><AdminUserDetail /></Suspense></AdminRoute>} />
        </Route>

        {/* Page Pricing — accessible connecté ou non */}
        <Route path="/pricing" element={<Suspense fallback={null}><Pricing /></Suspense>} />

        {/* Page publique intake client — pas de ProtectedRoute */}
        <Route path="/intake/:token" element={
          <Suspense fallback={
            <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc' }}>
              <div style={{ width: 32, height: 32, border: '4px solid #4F46E5', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
              <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
            </div>
          }>
            <ClientIntake />
          </Suspense>
        } />

        {/* Page publique déclaration freelance — pas de ProtectedRoute */}
        <Route path="/declaration/:token" element={
          <Suspense fallback={
            <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc' }}>
              <div style={{ width: 32, height: 32, border: '4px solid #4F46E5', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
              <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
            </div>
          }>
            <Declaration />
          </Suspense>
        } />

        {/* Page publique soumission partenaire externe — pas de ProtectedRoute */}
        <Route path="/partner/:token" element={
          <Suspense fallback={
            <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc' }}>
              <div style={{ width: 32, height: 32, border: '4px solid #4F46E5', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
              <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
            </div>
          }>
            <PartnerIntake />
          </Suspense>
        } />

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <Toaster />
    </BrowserRouter>
    </SentryErrorBoundary>
  )
}
